import { useState } from "react";
import { useAppContext } from "../../../context/AppContext";
import {
  AccessControlRecord,
  useGetAccessControlListQuery,
  useUpdateAccessControlMutation,
} from "../../../store/apiSlices/accessControl";
import { getStringColor } from "../../../utils";
import { CrossIcon, EditIcon, SpinnerIcon } from "../../Icons";

export default function AccessControlList({ tableId }: { tableId: number }) {
  const { data, isLoading, isError, refetch } = useGetAccessControlListQuery();

  if (isLoading)
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-20 bg-gray-200 rounded-xl"></div>
      </div>
    );

  if (isError)
    return (
      <div className="flex items-center justify-between bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
        <p className="text-gray-700 text-sm font-medium">
          Failed to load access list.
        </p>

        <button
          onClick={refetch}
          className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
        >
          Retry
        </button>
      </div>
    );

  const list = data?.records?.filter((el) => el.table_id === tableId) || [];

  return (
    <div className="space-y-3">
      <h3>People with access</h3>

      <Owner />

      {list.length > 0 &&
        list.map((item) => <AccessList key={item.id} item={item} />)}
    </div>
  );
}

function Owner() {
  const { user } = useAppContext();

  if (!user) return null;

  return (
    <div className="flex items-center justify-between py-1">
      {/* LEFT SIDE */}
      <div className="flex items-center gap-2">
        {/* Avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center 
                 text-white font-semibold text-sm shadow-sm shrink-0"
          style={{ backgroundColor: getStringColor(user.first_name) }}
        >
          {user.first_name.charAt(0)?.toUpperCase()}
        </div>

        {/* Name + You tag */}
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
            {`${user.first_name} ${user.last_name}`}
          </p>

          <span className="text-[10px] px-1.5 py-[1px] bg-gray-200 text-gray-700 rounded-md">
            You
          </span>
        </div>
      </div>

      {/* RIGHT SIDE ROLE */}
      <p className="text-xs font-semibold text-gray-600 px-2 py-1 bg-gray-100 rounded-lg">
        Owner
      </p>
    </div>
  );
}

function AccessList({ item }: { item: AccessControlRecord }) {
  const [isEditable, setIsEditable] = useState(false);

  const onEdit = () => setIsEditable(true);
  const onClose = () => setIsEditable(false);

  return (
    <div className="flex items-center justify-between py-1">
      {/* LEFT SIDE */}
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center 
                 text-white font-semibold text-sm shadow-sm shrink-0"
          style={{ backgroundColor: getStringColor(item.email) }}
        >
          {item.email.charAt(0)?.toUpperCase()}
        </div>

        <p className="text-sm font-medium text-gray-900">{item.email}</p>
      </div>

      <div className="flex items-center gap-2">
        {isEditable ? (
          <EditAccessControlRole item={item} onClose={onClose} />
        ) : (
          <>
            {/* Role Badge */}
            <p className="text-xs font-semibold text-gray-600 px-2 py-1 bg-gray-100 rounded-lg capitalize">
              {item.shared}
            </p>

            {/* Edit Button */}
            <button
              onClick={onEdit}
              className="p-1 rounded-md hover:bg-gray-200"
              title="Edit role"
            >
              <EditIcon className="w-4 h-4 text-gray-700" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function EditAccessControlRole({
  item,
  onClose,
}: {
  item: AccessControlRecord;
  onClose: () => void;
}) {
  const [updateAccessControl, { isLoading }] = useUpdateAccessControlMutation();

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    const payload = {
      id: item.id,
      shared: value,
    };

    await updateAccessControl(payload);

    onClose();
  };

  return (
    <>
      <p>
        {isLoading && (
          <SpinnerIcon className="size-3 text-gray-200 animate-spin fill-custom-blue-medium" />
        )}
      </p>
      <select
        disabled={isLoading}
        value={item.shared}
        onChange={handleChange}
        className="text-xs font-medium border border-gray-300 rounded-lg px-2 py-1 bg-white outline-none cursor-pointer disabled:cursor-not-allowed"
      >
        <option value="viewer">Viewer</option>
        <option value="editor">Editor</option>
      </select>

      <button
        onClick={onClose}
        className="p-1 rounded-md hover:bg-gray-200"
        title="Cancel"
      >
        <CrossIcon className="w-4 h-4 text-gray-600" />
      </button>
    </>
  );
}
